import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Document} from "../../../model/document";
import * as $ from 'jquery';
import {Order} from "../../../model/order";
import {DownloadService} from "../../../service/download.service";
import {ProductService} from "../../../service/product.service";
import {LoginIdService} from "../../../service/login-id.service";
import {StatusMessageService} from "../../../service/status-message.service";
import {StatusMessage} from "../../../model/status-message";
import {tap} from "rxjs/operators";
import StatusMessageUtils from "../../../utils/status-message-utils";
import DocumentFolderUtils from "../../../utils/document-folder-utils";
import {HttpHeaders} from "@angular/common/http";
import {CustomerInteraction} from "../../../model/customerInteraction";
import {StatusMessageComponent} from "../status-message/status-message.component";
import {Property} from "../../../model/property";
import CustomerInteractionUtils from "../../../utils/customer-interaction-utils";
import {Router} from "@angular/router";
import {InteractionStatus} from "../../../model/interactionStatus";
import ProductUtils from "../../../utils/product-utils";
import {Service} from "../../../model/service";
import NameFinderUtils from "../../../utils/name-finder-utils";
import {BookableService} from "../../../model/bookableService";
import {ServiceCategory} from "../../../model/serviceCategory";
import {BookableServiceService} from "../../../service/bookable-service.service";
import {DocumentStatusService} from "../../../service/document-status.service";
import * as Sentry from "@sentry/angular";

@Component({
  selector: 'app-document-row',
  templateUrl: './document-row.component.html',
  styleUrls: ['./document-row.component.css']
})
export class DocumentRowComponent implements OnInit {

  constructor(private downloadService: DownloadService,
              private productService: ProductService,
              private statusMessageService: StatusMessageService,
              private documentStatusService: DocumentStatusService,
              public loginIdService: LoginIdService,
              public router: Router, private bookableServiceService: BookableServiceService) {
  }

  @Input() document: Document;
  @Input() order: Order;
  @Input() customerInteractions: CustomerInteraction[];
  @Input() property: Property;
  @Output() updateBatchStarted: EventEmitter<any> = new EventEmitter();
  @ViewChild('statusMessageComponent') statusMessageComponent: StatusMessageComponent;
  products = [];
  batchStarted: boolean = false;
  isLoadingStatusMessages: boolean = false;
  statusMessageInitializationError: boolean = false;
  statusMessages: StatusMessage[] = [];
  bookableServicesByCategory: Map<string, BookableService[]> = new Map<string, BookableService[]>();
  serviceCategories: ServiceCategory[] = [];

  protected readonly StatusMessageUtils = StatusMessageUtils;

  ngOnInit(): void {
    this.initBookableServices().then( () => {
      this.productService.getProducts().subscribe(products => {
        this.products = products
      });
      this.initializeStatusMessages();
      this.document.subInformations = this.document.subInformations.sort((a, b) => a.nameLong.localeCompare(b.nameLong));
          console.log(this.document.selectedServices);
    });
  }

  /**
   * Initializes all service categories and all bookable services mapped by their service categories
   */
  public async initBookableServices(): Promise<void> {
    const getBookableServicesByCategoryObservable = this.bookableServiceService.getBookableServicesByCategoryFromStorageOrApi({headers: new HttpHeaders({'showing-spinner': ''})}).toPromise();
    const getServiceCategoriesObservable = this.bookableServiceService.getServiceCategoriesFromStorageOrApi({headers: new HttpHeaders({'showing-spinner': ''})}).toPromise();

    const [bookableServicesByCategory, serviceCategories] = await Promise.all([
      getBookableServicesByCategoryObservable,
      getServiceCategoriesObservable
    ]);

    this.bookableServicesByCategory = new Map<string, BookableService[]>(Object.entries(bookableServicesByCategory));
    this.serviceCategories = serviceCategories;
  }

  hasDocumentUnseenInformation(document: Document) {
    return !!document.statusInformation.find(s => s.seen === false);
  }

  /**
   * Toggles the visibility of the folder container element based on the given document id
   * Additionally updates the toggle button in the status message component
   *
   * @param docid Id of the document for which the folder container should be toggled.
   */
  public toggleFolderContainer(docid: string): void {
    const folderContainer = $(".folder-container[document-id='" + docid + "']");
    folderContainer.toggle();
    this.statusMessageComponent?.updateHistoryToggleButtonTopStyle();
    if (folderContainer.is(":visible")) {
      this.updateUnseenDocumentStatus(docid);
    }
  }

  /**
   * Marks unseen document status information as seen once the document row is opened.
   *
   * @param docid Id of the document for which unseen status information should be marked as seen.
   */
  private updateUnseenDocumentStatus(docid: string): void {
    if (!this.hasDocumentUnseenInformation(this.document)) {
      return;
    }

    this.documentStatusService.updateSeen(docid).subscribe({
      next: () => {
        this.document.statusInformation.forEach(statusInformation => statusInformation.seen = true);
      },
      error: error => Sentry.captureException(error)
    });
  }

  getLongName(shortName) {
    let result;
    this.products.forEach(product => {
      if (product.nameShort === shortName)
        result = product.name_long
      return;
    });
    return result ? result : shortName;
  }

  isBauakDocument(shortName: string) {
    return ProductUtils.BAUAK_DOCUMENT_NAMES.includes(shortName);
  }

  get copyService(): Service[] {
    return this.document.selectedServices.filter(service => service.nameShort !== 'benam');
  }

  get namingService(): Service | null {
    return this.document.selectedServices.find(service => service.nameShort === 'benam') ?? null;
  }

  hasAdditionalServices(selectedServices: Service[]) {
    return selectedServices.some(service => service.nameShort === 'benam')
  }

  /**
   * Initiates a download of all document files for a specific document.
   *
   * @param document Document for which all document files should be downloaded
   * @param event Event for which stopPropagation is called if the event is present
   */
  async downloadByDocument(document: Document, event: any = null) {
    if(event){
      event.stopPropagation();
    }
    const subFolderDepth = DocumentFolderUtils.getMaxSubfolderDepthInDocument(document);
    this.batchStarted = await this.downloadService.downloadDocument(document, document.isLoadingFilesAndFolder || subFolderDepth >= 2);
    this.updateBatchStarted.emit(this.batchStarted);
  }

  downloadDocumentFile(id) {
    this.downloadService.downloadDocumentFileById(id);
  }

  hasDownloadPermissions(): boolean {
    if(this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'DOWNLOAD');
    }
  }

  hasViewPermissions(): boolean {
    if(this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'VIEW');
    }
  }

  showToolTip() {
    return this.hasDownloadPermissions() ? '' : 'Sie haben nicht die erforderlichen Berechtigungen, um diese Dokumente herunterzuladen!';
  }

  /**
   * Checks if the document has any files either directly or within one of its folders
   *
   * @return True if the document has any files else false
   */
  public hasDocumentFiles(): boolean{
    if(!this.document.isLoadingFilesAndFolder){
      // Flatten the folders of the document to check for files within nested folders
      const flattenedFolders = DocumentFolderUtils.flattenFolders(this.document.folders);
      return (this.document.files?.length ?? 0) > 0 || flattenedFolders.some(folder => (folder.files?.length ?? 0) > 0);
    }
    return false;
  }

  /**
   * Determines if there are any relevant customer interactions associated with the current document.
   * Relevant customer interactions have to have the status 'OPEN'
   *
   * @return true if there is at least one relevant customer interaction within this.customerInteractions else false
   */
  public hasRelevantCustomerInteraction(): boolean {
    if (!this.customerInteractions || !CustomerInteractionUtils.hasOpenInteraction(this.customerInteractions)) {
      return false;
    }
    const openCustomerInteractions = this.customerInteractions.filter(customerInteraction => customerInteraction.status === InteractionStatus.OPEN);
    return openCustomerInteractions.some(customerInteraction => customerInteraction.associatedIds.includes(this.document.docid))
  }

  /**
   * Navigates to the latest relevant customer interaction associated with the current document.
   * If no relevant customer interaction is found nothing happens.
   *
   * @param event Event object used to stop propagation of the click event.
   */
  public navigateToCustomerInteraction(event: any): void{
    event.stopPropagation();
    const latestRelevantCustomerInteraction = CustomerInteractionUtils.getLatestCustomerInteractionAssociatedWithDocumentIds(this.customerInteractions, [this.document])
    if(!latestRelevantCustomerInteraction){
      return
    }
    this.router.navigate(
        [`/customer-interaction/${latestRelevantCustomerInteraction.id}`],
        { state: { property: this.property } }
    );
  }

  /**
   * Initializes status messages for this.document and sorts them by their date.
   * This function makes sure that there are always at least two status messages returned if only one was retrieved.
   */
  private initializeStatusMessages(): void {
    this.isLoadingStatusMessages = true;
    this.statusMessageService.getStatusMessagesByReferenceId(this.document.docid, {headers: new HttpHeaders({'showing-spinner': ''})}).pipe(
        tap({
          next: (statusMessages) => {
            this.statusMessages = statusMessages.map(message => {
              // Change the date of each status message to UTC to be able to display the correct date later
              message.date = new Date(message.date);
              return message;
            });

            // If there is only one status message for this.document duplicate this entry so it can be correctly displayed in the status message component
            if(this.statusMessages.length === 1){
              const duplicateMessage = Object.assign({}, this.statusMessages[0]);
              this.statusMessages.push(duplicateMessage);
            }

            this.statusMessages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            this.isLoadingStatusMessages = false;
            this.statusMessageInitializationError = false;
          },
          error: () => {
            this.isLoadingStatusMessages = false;
            this.statusMessageInitializationError = true;
          }
        })
    ).subscribe();
  }

  protected readonly ProductUtils = ProductUtils;
  protected readonly NameFinderUtils = NameFinderUtils;
}
